import React, { ReactNode } from "react";
import ProfileContainer from "./ProfileContainer";
import { useSelector } from "react-redux";
import { selectFromAmlStore } from "../amlSlice";
import { fullName, getEntityTypeCheckers } from "../utils/investor";
import { formatDate } from "../utils/date";
import {
  OnboardingModel,
  OnboardingModelName,
  StatusColor,
  StatusNaming,
} from "../constant/amlConstant";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { isFunction } from "lodash";
import { upliftNamings } from "../constant/upliftConstant";
import AmlActions from "../components/AmlActions";
import AmlStatusEditModal from "./AmlStatusEditModal";
import useBoolean from "../hooks/useBoolean";
import { EditPencil } from "iconoir-react";
import { usePermissionAction } from "../hooks/usePermissionAction";
import { useQuery } from "@tanstack/react-query";
import { getInvestorAmlSubject, GetInvestorAmlSubjectResponse } from "../api/aml";

type SubjectProfileProps = {
  personId: string;
};

type NotNull<T> = T extends null ? never : T;

type DerivedFn<R> = (
  data: NotNull<GetInvestorAmlSubjectResponse>
) => R;

type TableItem = {
  title: string;
  show?: DerivedFn<boolean>;
  loading: boolean;
  placeholder?: boolean;
  render: DerivedFn<ReactNode>;
};

const PLACEHOLDER = "-";
const VERTICAL_GUTTER = 19;

const kycReferencesSelector = selectFromAmlStore(
  (state) => state.kycReferences
);
const entityTypeSelector = selectFromAmlStore((state) => state.entityTypes);

const SubjectProfile: React.FC<SubjectProfileProps> = ({ personId }) => {
  const checkAmlUpdatePermission = usePermissionAction("update:investor-aml");
  const kycReferences = useSelector(kycReferencesSelector);
  const entityTypes = useSelector(entityTypeSelector);
  const entityTypeCheckers = getEntityTypeCheckers(entityTypes);
  const [editModalOpen, { setTrue: showEditModal, setFalse: closeEditModal }] =
    useBoolean();

  const {
    data: amlSubjectData,
    isLoading: amlSubjectLoading,
    refetch: refetchAmlSubject,
  } = useQuery({
    queryKey: ["investor-aml-subject", personId],
    queryFn: () => getInvestorAmlSubject(personId),
    staleTime: 60 * 1000, // Use cache, prevent multiple requests
  });

  const leftColumns: TableItem[] = [
    {
      title: "Person",
      loading: amlSubjectLoading,
      render: (detail) => <Typography>{detail.name}</Typography>,
    },
    {
      title: "Customer",
      loading: amlSubjectLoading,
      render: (detail) => <Typography>{detail.customer.name}</Typography>,
    },
    {
      title: "Onboarding Date",
      loading: amlSubjectLoading,
      render: (detail) => (
        <Typography>
          {detail.onboarding.date
            ? formatDate(detail.onboarding.date)
            : PLACEHOLDER}
        </Typography>
      ),
    },
    {
      title: "Onboarding Model",
      loading: amlSubjectLoading,
      render: (detail) => (
        <Typography>
          {detail.onboarding.model
            ? OnboardingModelName[detail.onboarding.model]
            : PLACEHOLDER}
        </Typography>
      ),
    },
    {
      title: "Uplift Date",
      loading: amlSubjectLoading,
      render: (detail) => {
        const dateStr = detail.uplift?.plannedDate
          ? formatDate(detail.uplift.plannedDate)
          : PLACEHOLDER;
        return <Typography>{dateStr}</Typography>;
      },
      show: (detail) => {
        return (
          !amlSubjectLoading &&
          detail.onboarding.model === OnboardingModel.RELIANCE
        );
      },
    },
    {
      title: "Verification Date",
      loading: amlSubjectLoading,
      render: (detail) => {
        return (
          <Typography>
            {detail.lastVerificationDate ? formatDate(detail.lastVerificationDate) : PLACEHOLDER}
          </Typography>
        );
      },
      show: (detail) => {
        return (
          !!detail?.lastVerificationDate
        );
      }
    },
  ];

  const rightColumns: TableItem[] = [
    {
      title: "Entity Type",
      loading: amlSubjectLoading,
      render: (detail) => {
        const subType = entityTypeCheckers.getCategoryOfType<string>(
          detail.entityType,
          "type"
        );
        return <Typography>{subType as string}</Typography>;
      },
    },
    {
      title: "Business Channel",
      loading: amlSubjectLoading,
      render: (detail)  => {
        let businessChannel = PLACEHOLDER;
        if (detail.businessChannel) {
          const targetReference = kycReferences.find(
            (el) => el.code === detail.businessChannel
          );
          if (targetReference) {
            businessChannel = targetReference.description;
          }
        }
        return <Typography>{businessChannel}</Typography>;
      },
    },
    {
      title: "Onboarding Account",
      loading: amlSubjectLoading,
      render: (detail) => {
        return (
          <Typography>{detail.onboarding.accountId || PLACEHOLDER}</Typography>
        );
      },
    },
    {
      title: "KYC Details",
      loading: amlSubjectLoading,
      render: (detail) => {
        let label: string;
        let color: string | undefined;

        if (detail.concessionStatus === "Y") {
          label = "Complete";
          color = "green";
        } else if (detail.concessionStatus === "N") {
          label = "Missing";
          color = "red";
        } else {
          label = "Not Required";
          color = undefined; // default text color
        }

        return (
          <Stack
            direction="row"
            columnGap="4px"
            sx={{ cursor: "default" }}
          >
            <Typography
              sx={{ color, whiteSpace: "nowrap" }}
            >
              {label}
            </Typography>
          </Stack>
        );
      },
    },
    {
      title: "Uplift Status",
      loading: amlSubjectLoading,
      render: (detail) => {
        return (
          <Typography>
            {detail.uplift?.upliftStatus
              ? upliftNamings[
                  detail.uplift?.upliftStatus as keyof typeof upliftNamings
              ]
              : PLACEHOLDER}
          </Typography>
        );
      },
      show: (detail) => {
        return (
          !amlSubjectLoading &&
          detail.onboarding.model === OnboardingModel.RELIANCE
        );
      },
    },
    {
      title: "Expiry Date",
      loading: amlSubjectLoading,
      render: (detail) => {
        return (
          <Typography>
            {detail.expiryDate ? formatDate(detail.expiryDate) : PLACEHOLDER}
          </Typography>
        );
      },
      show: (detail) => {
        return (
          !!detail?.lastVerificationDate
        );
      }
    },
    {
      title: "AML Status",
      loading: amlSubjectLoading,
      render: (detail) => {
        return (
          <Stack
            direction="row"
            columnGap="4px"
            onClick={checkAmlUpdatePermission(showEditModal)}
            sx={{ cursor: "pointer" }}
          >
            <Typography
              sx={{ color: StatusColor[detail.status], whiteSpace: "nowrap" }}
            >
              {StatusNaming[detail.status]}
            </Typography>
            <EditPencil />
          </Stack>
        );
      },
    },
  ];

  const renderSubjectItem = (item: TableItem, index: number, gap: number) => {
    if (item.placeholder) {
      return (
        <tr key={`placeholder-${index}`}>
          <th style={{ paddingTop: index === 0 ? 0 : gap }}>&nbsp;</th>
          <td>&nbsp;</td>
        </tr>
      );
    }
    return (
      <tr key={item.title || `row-${index}`}>
        <th
          style={{
            textAlign: "left",
            paddingLeft: "8px",
            paddingTop: index === 0 ? 0 : gap,
          }}
        >
          <Typography sx={{ fontStyle: "italic" }}>
            {item.title + ":"}
          </Typography>
        </th>
        <td
          style={{
            textAlign: "left",
            paddingLeft: "8px",
            paddingTop: index === 0 ? 0 : gap,
          }}
        >
          {item.loading ? (
            <CircularProgress size="1rem" />
          ) : (
            item.render(amlSubjectData!)
          )}
        </td>
      </tr>
    );
  };

  const renderSubjectTable = (columns: TableItem[]) => {
    return (
      <table>
        <tbody>
          {columns.map((el, index) => {
            const show =
              el.show === undefined ||
              (isFunction(el.show) &&
                el.show(amlSubjectData!));
            if (!show) {
              return null;
            }
            return renderSubjectItem(el, index, VERTICAL_GUTTER);
          })}
        </tbody>
      </table>
    );
  };

  const handleStatusEditSuccess = () => {
    closeEditModal();
    refetchAmlSubject();
  };

  return (
    <ProfileContainer id="subject-section" title="Subject and Onboarding">
      <Grid container rowGap="19px">
        <Grid item xs={12} sm={12} md={5.5}>
          {renderSubjectTable(leftColumns)}
        </Grid>
        <Grid item xs={12} sm={12} md={4.5}>
          {renderSubjectTable(rightColumns)}
        </Grid>
        {!amlSubjectLoading && (
          <Grid item xs={2}>
            <AmlActions personId={personId} />
          </Grid>
        )}
      </Grid>
      <AmlStatusEditModal
        personId={personId}
        open={editModalOpen}
        status={amlSubjectData?.status}
        onboardingModel={amlSubjectData?.onboarding.model}
        onCancel={closeEditModal}
        onSuccess={handleStatusEditSuccess}
      />
    </ProfileContainer>
  );
};

export default SubjectProfile;
